export type CrudAction = 'create' | 'read' | 'update' | 'delete';
export type PermissionAction =
  | CrudAction
  | 'assign_users'
  | 'assign_groups'
  | 'assign_roles'
  | 'assign_permissions'
  | 'change_password';
export type ResourceDomain = 'products' | 'services' | 'features' | 'users' | 'groups' | 'roles' | 'themes';

export type IamPermission = {
  id: string;
  action: PermissionAction;
  domain: ResourceDomain;
  resource: string;
};

export type IamRole = {
  id: string;
  name: string;
  description: string;
  permissions: string[];
};

export type GroupScope = {
  groupName: string;
  products: string[];
  services: string[];
  features: string[];
};

export type GroupRoleBinding = {
  groupName: string;
  roleIds: string[];
};

export type UserGroupBinding = {
  userId: string;
  groupNames: string[];
};

export type UserRoleBinding = {
  userId: string;
  roleIds: string[];
};

export type UserPermissionBinding = {
  userId: string;
  permissionIds: string[];
};

export type GroupPermissionBinding = {
  groupName: string;
  permissionIds: string[];
};

type MinimalMembership = {
  roles?: string[];
};

type MinimalUser = {
  platform_roles?: string[];
  effective_roles?: string[];
  companies?: MinimalMembership[];
};

const IAM_ROLES_STORAGE_KEY = 'nextcompli.iam.roles';
const IAM_SCOPES_STORAGE_KEY = 'nextcompli.iam.groupScopes';
const IAM_BINDINGS_STORAGE_KEY = 'nextcompli.iam.groupRoleBindings';
const IAM_USER_GROUP_BINDINGS_STORAGE_KEY = 'nextcompli.iam.userGroupBindings';
const IAM_USER_ROLE_BINDINGS_STORAGE_KEY = 'nextcompli.iam.userRoleBindings';
const IAM_USER_PERMISSION_BINDINGS_STORAGE_KEY = 'nextcompli.iam.userPermissionBindings';
const IAM_GROUP_PERMISSION_BINDINGS_STORAGE_KEY = 'nextcompli.iam.groupPermissionBindings';

export const ACTIONS: CrudAction[] = ['create', 'read', 'update', 'delete'];

const IDENTITY_ACTIONS: Record<'users' | 'groups' | 'roles', PermissionAction[]> = {
  users: ['create', 'read', 'update', 'delete', 'assign_groups', 'assign_roles', 'change_password'],
  groups: ['create', 'read', 'update', 'delete', 'assign_users', 'assign_roles', 'assign_permissions'],
  roles: ['create', 'read', 'update', 'delete', 'assign_permissions'],
};
export const DEFAULT_PRODUCTS = [
  'basic',
  'advanced',
  'enterprise',
  'basic support package',
  'advanced support package',
  'enterprise support package',
];
export const DEFAULT_SERVICES = [
  'screening operations',
  'data source health monitoring',
  'risk & exposure insights',
  'liability compliance monitoring',
  'audit evidence management',
  'alerts & incident workflow',
  'executive compliance reporting',
];
export const DEFAULT_FEATURES = [
  'real-time + batch screening',
  'fuzzy matching & transliteration',
  'audit trail + alert workflow',
  'usage-based pricing plans',
  'research reports',
];
export const DEFAULT_ADMIN_RESOURCES = {
  users: ['users'],
  groups: ['groups'],
  roles: ['roles'],
  themes: ['website-theming'],
} as const;

const normalizeTokenList = (raw: string[]): string[] =>
  Array.from(new Set(raw.map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

const readJson = <T>(storageKey: string, fallback: T): T => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return fallback;
    }
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const writeJson = <T>(storageKey: string, payload: T): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(storageKey, JSON.stringify(payload));
};

export const toPermissionId = (action: PermissionAction, domain: ResourceDomain, resource: string): string =>
  `${action}:${domain}:${resource.trim().toLowerCase()}`;

export const parseCsvTokens = (raw: string): string[] =>
  normalizeTokenList(raw.split(',').map((item) => item.trim().toLowerCase()));

export const loadIamRoles = (): IamRole[] =>
  readJson<IamRole[]>(IAM_ROLES_STORAGE_KEY, []);

export const saveIamRoles = (roles: IamRole[]): void => {
  writeJson(IAM_ROLES_STORAGE_KEY, roles);
};

export const loadGroupScopes = (): GroupScope[] =>
  readJson<GroupScope[]>(IAM_SCOPES_STORAGE_KEY, []);

export const saveGroupScopes = (scopes: GroupScope[]): void => {
  writeJson(IAM_SCOPES_STORAGE_KEY, scopes);
};

export const loadGroupRoleBindings = (): GroupRoleBinding[] =>
  readJson<GroupRoleBinding[]>(IAM_BINDINGS_STORAGE_KEY, []);

export const saveGroupRoleBindings = (bindings: GroupRoleBinding[]): void => {
  writeJson(IAM_BINDINGS_STORAGE_KEY, bindings);
};

export const loadUserGroupBindings = (): UserGroupBinding[] =>
  readJson<UserGroupBinding[]>(IAM_USER_GROUP_BINDINGS_STORAGE_KEY, []);

export const saveUserGroupBindings = (bindings: UserGroupBinding[]): void => {
  writeJson(IAM_USER_GROUP_BINDINGS_STORAGE_KEY, bindings);
};

export const loadUserRoleBindings = (): UserRoleBinding[] =>
  readJson<UserRoleBinding[]>(IAM_USER_ROLE_BINDINGS_STORAGE_KEY, []);

export const saveUserRoleBindings = (bindings: UserRoleBinding[]): void => {
  writeJson(IAM_USER_ROLE_BINDINGS_STORAGE_KEY, bindings);
};

export const loadUserPermissionBindings = (): UserPermissionBinding[] =>
  readJson<UserPermissionBinding[]>(IAM_USER_PERMISSION_BINDINGS_STORAGE_KEY, []);

export const saveUserPermissionBindings = (bindings: UserPermissionBinding[]): void => {
  writeJson(IAM_USER_PERMISSION_BINDINGS_STORAGE_KEY, bindings);
};

export const loadGroupPermissionBindings = (): GroupPermissionBinding[] =>
  readJson<GroupPermissionBinding[]>(IAM_GROUP_PERMISSION_BINDINGS_STORAGE_KEY, []);

export const saveGroupPermissionBindings = (bindings: GroupPermissionBinding[]): void => {
  writeJson(IAM_GROUP_PERMISSION_BINDINGS_STORAGE_KEY, bindings);
};

export const buildPermissionMatrix = (
  products: string[] = DEFAULT_PRODUCTS,
  services: string[] = DEFAULT_SERVICES,
  features: string[] = DEFAULT_FEATURES,
): IamPermission[] => {
  const standardDomains: Array<{ domain: ResourceDomain; resources: string[] }> = [
    { domain: 'products', resources: products },
    { domain: 'services', resources: services },
    { domain: 'features', resources: features },
    { domain: 'themes', resources: [...DEFAULT_ADMIN_RESOURCES.themes] },
  ];

  const standardPermissions = standardDomains.flatMap(({ domain, resources }) =>
    normalizeTokenList(resources).flatMap((resource) =>
      ACTIONS.map((action) => ({
        id: toPermissionId(action, domain, resource),
        action,
        domain,
        resource,
      })),
    ),
  );

  const identityPermissions = (Object.keys(IDENTITY_ACTIONS) as Array<'users' | 'groups' | 'roles'>).flatMap((domain) =>
    normalizeTokenList([...DEFAULT_ADMIN_RESOURCES[domain]]).flatMap((resource) =>
      IDENTITY_ACTIONS[domain].map((action) => ({
        id: toPermissionId(action, domain, resource),
        action,
        domain,
        resource,
      })),
    ),
  );

  return [...standardPermissions, ...identityPermissions];
};

export const roleSlug = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `role-${Date.now()}`;

export const inferUserGroups = (user: MinimalUser | null): string[] => {
  if (!user) {
    return [];
  }

  const roleSet = new Set<string>([
    ...(user.platform_roles || []),
    ...(user.effective_roles || []),
    ...(user.companies || []).flatMap((membership) => membership.roles || []),
  ]);

  const groups = new Set<string>();

  const hasSalesRole = Array.from(roleSet).some((role) => role.startsWith('sales_') || role === 'sales');
  if (hasSalesRole) {
    groups.add('sales');
  }

  const hasOpsRole = Array.from(roleSet).some((role) => role.startsWith('ops') || role === 'platform_admin');
  if (hasOpsRole) {
    groups.add('ops');
  }

  return Array.from(groups);
};

export const resolveSalesScope = (scopes: GroupScope[]): GroupScope | null => {
  const salesScope = scopes.find((scope) => scope.groupName.trim().toLowerCase() === 'sales');
  return salesScope || null;
};

export const matchesScopedResource = (resourceName: string, scopedValues: string[]): boolean => {
  if (!scopedValues.length) {
    return false;
  }
  const normalizedName = resourceName.trim().toLowerCase();
  return scopedValues.some((value) => normalizedName.includes(value));
};
